import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Link,
  Paper,
  IconButton,
  Tooltip,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  Alert,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useMediaQuery,
  useTheme,
  Pagination
} from '@mui/material';
import {
  School as SchoolIcon,
  LocationOn,
  Language,
  People,
  AttachMoney,
  SportsSoccer,
  Event,
  School as ProgramIcon,
  Star,
  StarBorder,
} from '@mui/icons-material';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import { School } from '../../types/index';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Avatar from '@mui/material/Avatar';
import PersonIcon from '@mui/icons-material/Person';
import { ref as dbRef, get as dbGet, set, remove } from 'firebase/database';
import { database, auth } from '../../firebase';
import { useAuthState } from '../../hooks/useAuthState';
import { calculateNextSortOrder } from '../../utils/favoritesUtils';
import { Student } from '../../types/Student';

import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';

import { useEvents } from '../../contexts/EventsContext';
import { useSchools } from '../../contexts/SchoolsContext';
import { usePrograms } from '../../contexts/ProgramsContext';
import { useUser } from '../../contexts/UserContext';
import { activityLogService } from '../../services/activityLogService';
import { calculateDistance, formatDistance } from '../../utils/distance';
import { getHighSchoolLocation, getHighSchoolLocationByName } from '../../utils/highSchoolLocation';
import MapModal from '../MapModal';
import CustomEligibilityIndicatorsModal from '../CustomEligibilityIndicatorsModal';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HelpIcon from '@mui/icons-material/Help';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import UndoIcon from '@mui/icons-material/Undo';
// Removed: import { useScholarships } from '../../contexts/ScholarshipsContext';

// TikTok SVG icon
const TikTokIcon = (props: any) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M21 8.13c-1.13 0-2.19-.37-3.05-1V15.5c0 3.04-2.46 5.5-5.5 5.5S7 18.54 7 15.5 9.46 10 12.5 10c.17 0 .33.01.5.03V12c-.17-.02-.33-.03-.5-.03-1.93 0-3.5 1.57-3.5 3.5s1.57 3.5 3.5 3.5 3.5-1.57 3.5-3.5V3h2c0 2.84 2.31 5.13 5 5.13z" fill="#000"/>
  </svg>
);

// X (Twitter) SVG icon
const XIcon = (props: any) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" fill="currentColor"/>
  </svg>
);

const socialIconMap: Record<string, React.ReactNode> = {
  Twitter: <XIcon color="primary" />,
  Facebook: <FacebookIcon color="primary" />,
  Instagram: <InstagramIcon sx={{ color: '#E1306C' }} />,
  TikTok: <TikTokIcon style={{ color: '#000' }} />,
};

const formatDate = (date: string) => {
  const dt = new Date(`${date}T00:00`);
  const m = dt.getMonth() + 1;
  const d = dt.getDate();
  const y = dt.getFullYear().toString().slice(-2);
  return `${m}/${d}/${y}`;
};

const formatTime = (time: string) => {
  const [h, min] = time.split(":").map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  let hour = h % 12;
  if (hour === 0) hour = 12;
  return `${hour}:${min.toString().padStart(2, '0')} ${ampm}`;
};

// Helper to format date as m/d/yyyy
const formatEventDate = (dateStr: string) => {
  if (!dateStr) return '';
  const dt = new Date(dateStr.replace(' ', 'T'));
  if (isNaN(dt.getTime())) return '';
  const m = dt.getMonth() + 1;
  const d = dt.getDate();
  const y = dt.getFullYear();
  return `${m}/${d}/${y}`;
};

// Helper to format time as h:mm am/pm
const formatEventTime = (dateStr: string) => {
  if (!dateStr) return '';
  const dt = new Date(dateStr.replace(' ', 'T'));
  if (isNaN(dt.getTime())) return '';
  let hour = dt.getHours();
  const min = dt.getMinutes();
  const ampm = hour >= 12 ? 'PM' : 'AM';
  hour = hour % 12;
  if (hour === 0) hour = 12;
  return `${hour}:${min.toString().padStart(2, '0')} ${ampm}`;
};

// Helper to format date as mmm dd yyyy with time in parentheses
const formatEventDateWithTime = (dateStr: string) => {
  if (!dateStr) return '';
  const dt = new Date(dateStr.replace(' ', 'T'));
  if (isNaN(dt.getTime())) return '';
  
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = months[dt.getMonth()];
  const day = dt.getDate();
  const year = dt.getFullYear();
  
  let hour = dt.getHours();
  const min = dt.getMinutes();
  const ampm = hour >= 12 ? 'PM' : 'AM';
  hour = hour % 12;
  if (hour === 0) hour = 12;
  const time = `${hour}:${min.toString().padStart(2, '0')}${ampm}`;
  
  return `${month} ${day} ${year} (${time})`;
};

// Helper to ensure a website URL starts with http(s)://
function formatWebsiteUrl(url?: string): string {
  if (!url) return '';
  
  // If the URL already has a protocol, return as is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  
  // Otherwise, add https://
  return `https://${url}`;
}

const SchoolProfile: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { schools, loading: schoolsLoading, error: schoolsError } = useSchools();
  const { user, isAuthStateStable } = useAuthState();

  const { events: allEvents, loading: eventsLoading } = useEvents();
  const { 
    programs, 
    loading: programsLoading, 
    error: programsError,
    eligibilityLoading,
    eligibilityError,
    calculateEligibilityForSchools,
    isEligibilityCalculatedForSchools,
    getProgramEligibility,
    clearEligibility
  } = usePrograms();
  const { user: currentUser } = useUser();

  // Helper function to get the correct spelling based on user's country
  const getFavouritesLabel = () => {
    return currentUser?.country === 'US' ? 'Favorites' : 'Favourites';
  };
  

  // Removed: useScholarships, scholarships, scholarshipsLoading, scholarshipsError
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Removed pagination for programs - all programs will show on one page
  // Removed: scholarshipsPage, SCHOLARSHIPS_PER_PAGE
  const [selectedTab, setSelectedTab] = useState(() => {
    // Check if we're returning from EventDetail with a specific tab
    if (location.state?.selectedTab !== undefined) {
      return location.state.selectedTab;
    }
    // Check for tab query parameter
    const tabParam = searchParams.get('tab');
    if (tabParam === 'events') return 1;
    if (tabParam === 'programs') return 0;
    return 0;
  });

  // Handle location state changes for tab selection
  useEffect(() => {
    if (location.state?.selectedTab !== undefined) {
      setSelectedTab(location.state.selectedTab);
      // Clear the state to prevent it from persisting
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  // Handle URL parameter changes for tab selection
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'events') setSelectedTab(1);
    else if (tabParam === 'programs') setSelectedTab(0);
  }, [searchParams]);
  const [userSchoolName, setUserSchoolName] = useState('');
  const [athletics, setAthletics] = useState<{ sport: string; gender: string }[]>([]);
  const [athleticsLoading, setAthleticsLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);
  const [favoriteLoading, setFavoriteLoading] = useState(false);
  const [distance, setDistance] = useState<number | null>(null);
  const [distanceLoading, setDistanceLoading] = useState(false);
  
  // Filter state with URL persistence
  const getFilterFromURL = (key: string, defaultValue: string) => {
    const urlValue = searchParams.get(key);
    if (urlValue !== null) return urlValue;
    
    // Fallback to localStorage
    const storedValue = localStorage.getItem(`schoolProfile_${id}_${key}`);
    return storedValue || defaultValue;
  };

  const [statusFilter, setStatusFilter] = useState(() => getFilterFromURL('status', 'All'));
  const [categoryFilter, setCategoryFilter] = useState(() => getFilterFromURL('category', ''));
  const [programNameFilter, setProgramNameFilter] = useState(() => getFilterFromURL('program', ''));

  // Pagination state for mobile programs view
  const [programsPage, setProgramsPage] = useState(1);
  const PROGRAMS_PER_PAGE = 10;

  // Map modal state
  const [mapModalOpen, setMapModalOpen] = useState(false);
  
  // Custom Eligibility Indicators modal state
  const [eligibilityIndicatorsModalOpen, setEligibilityIndicatorsModalOpen] = useState(false);

  // Wrapper functions to update both state and URL/localStorage
  const updateStatusFilter = (value: string) => {
    console.log('🔄 updateStatusFilter called with value:', value);
    console.log('🔄 updateStatusFilter - before update:', {
      currentStatusFilter: statusFilter,
      currentSearchParams: searchParams.toString()
    });
    
    setStatusFilter(value);
    const newSearchParams = new URLSearchParams(searchParams);
    if (value === 'All') {
      newSearchParams.delete('status');
    } else {
      newSearchParams.set('status', value);
    }
    setSearchParams(newSearchParams);
    localStorage.setItem(`schoolProfile_${id}_status`, value);
    
    console.log('🔄 updateStatusFilter - after update:', {
      newValue: value,
      newSearchParams: newSearchParams.toString()
    });
  };

  const updateCategoryFilter = (value: string) => {
    setCategoryFilter(value);
    const newSearchParams = new URLSearchParams(searchParams);
    if (value === '') {
      newSearchParams.delete('category');
    } else {
      newSearchParams.set('category', value);
    }
    setSearchParams(newSearchParams);
    localStorage.setItem(`schoolProfile_${id}_category`, value);
  };

  const updateProgramNameFilter = (value: string) => {
    setProgramNameFilter(value);
    const newSearchParams = new URLSearchParams(searchParams);
    if (value === '') {
      newSearchParams.delete('program');
    } else {
      newSearchParams.set('program', value);
    }
    setSearchParams(newSearchParams);
    localStorage.setItem(`schoolProfile_${id}_program`, value);
  };

  // Function to handle back navigation
  const handleBackNavigation = () => {
    // Check if we came from EdVisor
    const fromEdvisor = searchParams.get('fromEdvisor');
    if (fromEdvisor === '1') {
      console.log('🔄 SchoolProfile: Navigating back to EdVisor');
      // If we came from EdVisor, navigate back to EdVisor
      navigate('/edvisor');
      return;
    }
    
    // Check if we came from advanced search (either program or school navigation)
    const returningFromAdvancedSearch = sessionStorage.getItem('returningFromAdvancedSearch');
    const returningFromSchoolProfile = sessionStorage.getItem('returningFromSchoolProfile');
    
    console.log('🔄 SchoolProfile: Back navigation check:', {
      returningFromAdvancedSearch,
      returningFromSchoolProfile,
      fromEdvisor
    });
    
    if (returningFromAdvancedSearch === 'true' || returningFromSchoolProfile === 'true') {
      console.log('🔄 SchoolProfile: Navigating back to Advanced Search');
      // If we came from advanced search, navigate back to advanced search
      // Don't clear the flags here - let Advanced Search handle it after detecting them
      navigate('/advanced-search');
      return;
    }
    
    // Check if we came from guidance dashboard
    const fromGuidance = searchParams.get('fromGuidance');
    if (fromGuidance === '1') {
      // Navigate back to guidance dashboard
      navigate('/guidance-dashboard');
      return;
    }
    
    // Check if we came from a student profile (guidance user viewing student)
    const fromStudent = searchParams.get('fromStudent');
    if (fromStudent) {
      // Navigate back to the student profile page
      navigate(`/students/${fromStudent}`);
      return;
    }
    
    // Check if we came from a program profile
    const fromProgram = searchParams.get('fromProgram');
    if (fromProgram) {
      // Navigate back to the specific program profile
      const currentParams = new URLSearchParams(searchParams);
      currentParams.delete('fromProgram'); // Remove the fromProgram parameter
      navigate(`/programs/${fromProgram}?${currentParams.toString()}`);
    } else if (location.state?.from === 'dashboard') {
      // If we came from the dashboard, navigate back to dashboard
      navigate('/');
    } else {
      // Preserve filter state when navigating back to schools list
      const currentParams = new URLSearchParams(searchParams);
      // Remove school-specific filter parameters
      currentParams.delete('status');
      currentParams.delete('category');
      currentParams.delete('program');
      
      // Navigate back to schools page with preserved filters
      navigate(`/schools?${currentParams.toString()}`);
    }
  };

  // Get the school from context
  const school = useMemo(() => {
    return schools.find(s => s.id === id) || null;
  }, [schools, id]);

  // Log school pageview when school is loaded
  useEffect(() => {
    if (school && id && currentUser?.uid) {
      try {
        activityLogService.logSchoolPageview(currentUser.uid, id);
      } catch (error) {
        console.error('Error logging school pageview activity:', error);
        // Don't block the flow if logging fails
      }
    }
  }, [school, id, currentUser?.uid]);



  // Get programs for this school from context
  const schoolPrograms = useMemo(() => {
    if (!school) return [];
    return programs.filter(p => p.schoolName === school.name);
  }, [programs, school]);

  // Removed: schoolScholarships, filteredScholarships, paginatedScholarships



  // Check if current user has this school as favorite
  const checkIfFavorite = async () => {
    try {
      const currentUser = auth.currentUser;
      
      // Clear favorite status if no user, no school, or auth state not stable
      if (!currentUser || !school || !isAuthStateStable) {
        console.log('🔄 SchoolProfile: Clearing favorite status - no user, no school, or auth state not stable');
        setIsFavorite(false);
        return;
      }

      console.log('🔄 SchoolProfile: Checking favorite status for user:', currentUser.uid, 'school:', school.name);

      const favoriteRef = dbRef(database, `favoriteSchoolProgram/${school.name}/${school.name}/${currentUser.uid}`);
      const snapshot = await dbGet(favoriteRef);
      const isFav = snapshot.exists();
      setIsFavorite(isFav);
      console.log('✅ SchoolProfile: Favorite status:', isFav);
    } catch (error) {
      console.error('Error checking favorite status:', error);
      setIsFavorite(false);
    }
  };

  // Toggle favorite status
  const toggleFavorite = async () => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser || !school) return;

      setFavoriteLoading(true);
      const favoriteRef = dbRef(database, `favoriteSchoolProgram/${school.name}/${school.name}/${currentUser.uid}`);
      const userFavoritesRef = dbRef(database, `user/${currentUser.uid}/favorites/${school.name}`);

      if (isFavorite) {
        // Remove from favorites
        await Promise.all([
          remove(favoriteRef),
          remove(userFavoritesRef)
        ]);
        setIsFavorite(false);
        
        // Log the school removed from favorites activity
        try {
          await activityLogService.logSchoolRemovedFromFavorites(currentUser.uid, school.id);
        } catch (error) {
          console.error('Error logging school removed from favorites activity:', error);
          // Don't block the flow if logging fails
        }
      } else {
        // Add to favorites
        const timestamp = new Date().toISOString();
        const sortOrder = await calculateNextSortOrder(currentUser.uid);
        
        await Promise.all([
          set(favoriteRef, true),
          set(userFavoritesRef, {
            FavoritedImageURL: school.image || '',
            FavoritedLocation: `${school.city}, ${school.province}`,
            FavoritedName: school.name,
            FavoritedSortOrder: sortOrder,
            FavoritedTimestamp: timestamp,
            FavoritedType: 'school',
            FavoritedUID: currentUser.uid,
            SchoolID: school.id
          })
        ]);
        setIsFavorite(true);
        
        // Log the school added to favorites activity
        try {
          await activityLogService.logSchoolAddedToFavorites(currentUser.uid, school.id);
        } catch (error) {
          console.error('Error logging school added to favorites activity:', error);
          // Don't block the flow if logging fails
        }
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    } finally {
      setFavoriteLoading(false);
    }
  };

  // Handle school website click
  const handleSchoolWebsiteClick = async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (school?.website && currentUser?.uid) {
      // Log the external school website referral activity
      try {
        await activityLogService.logExternalSchoolWebsiteReferral(currentUser.uid, school.id);
      } catch (error) {
        console.error('Error logging external school website referral activity:', error);
        // Don't block the flow if logging fails
      }
      
      // Format the URL properly
      const formattedUrl = formatWebsiteUrl(school.website);
      
      // Try different approaches for mobile vs desktop
      try {
        if (isMobile) {
          // For mobile, try using location.href first, then fallback to window.open
          window.location.href = formattedUrl;
        } else {
          // For desktop, use window.open
          window.open(formattedUrl, '_blank');
        }
      } catch (error) {
        console.error('Error opening external link:', error);
        // Fallback: try window.open for all devices
        window.open(formattedUrl, '_blank');
      }
    }
  };

  // Handle school catalog/viewbook click
  const handleSchoolCatalogClick = async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (school?.SchoolCatalog && currentUser?.uid) {
      // Log the external school catalog referral activity
      try {
        await activityLogService.logExternalSchoolWebsiteReferral(currentUser.uid, school.id);
      } catch (error) {
        console.error('Error logging external school catalog referral activity:', error);
        // Don't block the flow if logging fails
      }
      
      // Format the URL properly
      const formattedUrl = formatWebsiteUrl(school.SchoolCatalog);
      
      // Try different approaches for mobile vs desktop
      try {
        if (isMobile) {
          // For mobile, try using location.href first, then fallback to window.open
          window.location.href = formattedUrl;
        } else {
          // For desktop, use window.open
          window.open(formattedUrl, '_blank');
        }
      } catch (error) {
        console.error('Error opening external link:', error);
        // Fallback: try window.open for all devices
        window.open(formattedUrl, '_blank');
      }
    }
  };

  // Handle virtual tour click
  const handleVirtualTourClick = async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (school?.SchoolVirtualTourURL && currentUser?.uid) {
      // Log the external virtual tour referral activity
      try {
        await activityLogService.logExternalSchoolWebsiteReferral(currentUser.uid, school.id);
      } catch (error) {
        console.error('Error logging external virtual tour referral activity:', error);
        // Don't block the flow if logging fails
      }
      
      // Format the URL properly
      const formattedUrl = formatWebsiteUrl(school.SchoolVirtualTourURL);
      
      // Try different approaches for mobile vs desktop
      try {
        if (isMobile) {
          // For mobile, try using location.href first, then fallback to window.open
          window.location.href = formattedUrl;
        } else {
          // For desktop, use window.open
          window.open(formattedUrl, '_blank');
        }
      } catch (error) {
        console.error('Error opening external link:', error);
        // Fallback: try window.open for all devices
        window.open(formattedUrl, '_blank');
      }
    }
  };

  // Handle campus tour click
  const handleCampusTourClick = async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (school?.SchoolCampusTourURL && currentUser?.uid) {
      // Log the external campus tour referral activity
      try {
        await activityLogService.logExternalSchoolWebsiteReferral(currentUser.uid, school.id);
      } catch (error) {
        console.error('Error logging external campus tour referral activity:', error);
        // Don't block the flow if logging fails
      }
      
      // Format the URL properly
      const formattedUrl = formatWebsiteUrl(school.SchoolCampusTourURL);
      
      // Try different approaches for mobile vs desktop
      try {
        if (isMobile) {
          // For mobile, try using location.href first, then fallback to window.open
          window.location.href = formattedUrl;
        } else {
          // For desktop, use window.open
          window.open(formattedUrl, '_blank');
        }
      } catch (error) {
        console.error('Error opening external link:', error);
        // Fallback: try window.open for all devices
        window.open(formattedUrl, '_blank');
      }
    }
  };

  // Handle athletics website click
  const handleAthleticsWebsiteClick = async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (school?.athleticsWebsite && currentUser?.uid) {
      // Log the external athletics website referral activity
      try {
        await activityLogService.logExternalAthleticsWebsiteReferral(currentUser.uid, school.id);
      } catch (error) {
        console.error('Error logging external athletics website referral activity:', error);
        // Don't block the flow if logging fails
      }
      
      // Format the URL properly
      const formattedUrl = formatWebsiteUrl(school.athleticsWebsite);
      
      // Try different approaches for mobile vs desktop
      try {
        if (isMobile) {
          // For mobile, try using location.href first, then fallback to window.open
          window.location.href = formattedUrl;
        } else {
          // For desktop, use window.open
          window.open(formattedUrl, '_blank');
        }
      } catch (error) {
        console.error('Error opening external link:', error);
        // Fallback: try window.open for all devices
        window.open(formattedUrl, '_blank');
      }
    }
  };

  // Handle school Twitter click
  const handleSchoolTwitterClick = async () => {
    if (school?.SchoolTwitterURL && currentUser?.uid) {
      // Log the external school Twitter referral activity
      try {
        await activityLogService.logExternalSchoolTwitterReferral(currentUser.uid, school.id);
      } catch (error) {
        console.error('Error logging external school Twitter referral activity:', error);
        // Don't block the flow if logging fails
      }
      
      // Try different approaches for mobile vs desktop
      try {
        if (isMobile) {
          // For mobile, try using location.href first, then fallback to window.open
          window.location.href = school.SchoolTwitterURL;
        } else {
          // For desktop, use window.open
          window.open(school.SchoolTwitterURL, '_blank');
        }
      } catch (error) {
        console.error('Error opening external link:', error);
        // Fallback: try window.open for all devices
        window.open(school.SchoolTwitterURL, '_blank');
      }
    }
  };

  // Handle school Instagram click
  const handleSchoolInstagramClick = async () => {
    if (school?.SchoolInstagramURL && currentUser?.uid) {
      // Log the external school Instagram referral activity
      try {
        await activityLogService.logExternalSchoolInstagramReferral(currentUser.uid, school.id);
      } catch (error) {
        console.error('Error logging external school Instagram referral activity:', error);
        // Don't block the flow if logging fails
      }
      
      // Try different approaches for mobile vs desktop
      try {
        if (isMobile) {
          // For mobile, try using location.href first, then fallback to window.open
          window.location.href = school.SchoolInstagramURL;
        } else {
          // For desktop, use window.open
          window.open(school.SchoolInstagramURL, '_blank');
        }
      } catch (error) {
        console.error('Error opening external link:', error);
        // Fallback: try window.open for all devices
        window.open(school.SchoolInstagramURL, '_blank');
      }
    }
  };

  // Handle school Wikipedia click
  const handleSchoolWikipediaClick = async () => {
    if (school?.SchoolWikiURL && currentUser?.uid) {
      // Log the external school Wikipedia referral activity
      try {
        await activityLogService.logExternalSchoolWikipediaReferral(currentUser.uid, school.id);
      } catch (error) {
        console.error('Error logging external school Wikipedia referral activity:', error);
        // Don't block the flow if logging fails
      }
      
      // Format the Wikipedia URL properly
      const wikiUrl = school.SchoolWikiURL?.startsWith('http') ? school.SchoolWikiURL : `https://${school.SchoolWikiURL}`;
      
      // Try different approaches for mobile vs desktop
      try {
        if (isMobile) {
          // For mobile, try using location.href first, then fallback to window.open
          window.location.href = wikiUrl || '';
        } else {
          // For desktop, use window.open
          window.open(wikiUrl || '', '_blank');
        }
      } catch (error) {
        console.error('Error opening external link:', error);
        // Fallback: try window.open for all devices
        window.open(wikiUrl || '', '_blank');
      }
    }
  };

  // Handle school YouTube click
  const handleSchoolYouTubeClick = async () => {
    if (school?.SchoolYoutubeURL && currentUser?.uid) {
      // Log the external school YouTube referral activity
      try {
        await activityLogService.logExternalSchoolYouTubeReferral(currentUser.uid, school.id);
      } catch (error) {
        console.error('Error logging external school YouTube referral activity:', error);
        // Don't block the flow if logging fails
      }
      
      // Format the YouTube URL properly
      const youtubeUrl = school.SchoolYoutubeURL?.startsWith('http') ? school.SchoolYoutubeURL : `https://${school.SchoolYoutubeURL}`;
      
      // Try different approaches for mobile vs desktop
      try {
        if (isMobile) {
          // For mobile, try using location.href first, then fallback to window.open
          window.location.href = youtubeUrl || '';
        } else {
          // For desktop, use window.open
          window.open(youtubeUrl || '', '_blank');
        }
      } catch (error) {
        console.error('Error opening external link:', error);
        // Fallback: try window.open for all devices
        window.open(youtubeUrl || '', '_blank');
      }
    }
  };

  // Fetch athletics from database
  useEffect(() => {
    const fetchData = async () => {
      if (!id || !school) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Get the current user for authentication check
        const currentUser = auth.currentUser;
        if (!currentUser) {
          setError('No authenticated user found');
          setLoading(false);
          return;
        }

        console.log('✅ UPDATED CODE: Fetching additional data for school:', school.name);

        // Fetch athletics for this school
        const fetchAthletics = async () => {
          if (!school) return;
          setAthleticsLoading(true);
          try {
            const athleticsRef = dbRef(database, 'athletics');
            const athleticsSnap = await dbGet(athleticsRef);
            if (athleticsSnap.exists()) {
              const athleticsData = athleticsSnap.val();
              const filtered = Object.values(athleticsData).filter((a: any) => a.AthleticsSchoolName === school.name);
              setAthletics(filtered.map((a: any) => ({
                sport: a.AthleticsSport,
                gender: a.AthleticsGender === 'M' ? 'Male'
                      : a.AthleticsGender === 'W' ? 'Female'
                      : a.AthleticsGender // fallback for 'Both' or other values
              })));
            } else {
              setAthletics([]);
            }
          } catch {
            setAthletics([]);
          } finally {
            setAthleticsLoading(false);
          }
        };
        fetchAthletics();

      } catch (err) {
        console.error('Error fetching school data:', err);
        setError('Failed to load school data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (!schoolsLoading && school) {
      fetchData();
    }
  }, [id, school, schoolsLoading]);

  // Scroll to top when component mounts or school ID changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  // Calculate distance from user's high school to this school
  const calculateDistanceFromUserSchool = async () => {
    if (!currentUser || !school) return;
    
    setDistanceLoading(true);
    try {
      let userHighSchoolLocation = null;
      
      // Try to get location by ID first
      if (currentUser.schoolID) {
        userHighSchoolLocation = await getHighSchoolLocation(currentUser.schoolID);
      }
      
      // If not found by ID, try by name
      if (!userHighSchoolLocation && currentUser.schoolName) {
        userHighSchoolLocation = await getHighSchoolLocationByName(currentUser.schoolName);
      }
      
      if (userHighSchoolLocation && school.SchoolLocationLatitude && school.SchoolLocationLongitude) {
        const schoolLat = parseFloat(school.SchoolLocationLatitude);
        const schoolLon = parseFloat(school.SchoolLocationLongitude);
        
        if (!isNaN(schoolLat) && !isNaN(schoolLon)) {
          const calculatedDistance = calculateDistance(
            userHighSchoolLocation.latitude,
            userHighSchoolLocation.longitude,
            schoolLat,
            schoolLon
          );
          setDistance(calculatedDistance);
        }
      }
    } catch (error) {
      console.error('Error calculating distance:', error);
    } finally {
      setDistanceLoading(false);
    }
  };

  // Check favorite status when school loads and auth state is stable
  useEffect(() => {
    if (school && !schoolsLoading) {
      checkIfFavorite();
      calculateDistanceFromUserSchool();
    }
  }, [school, schoolsLoading, currentUser?.schoolID, currentUser?.schoolName, user, isAuthStateStable]);

  // Check eligibility when school loads and user is available
  useEffect(() => {
    console.log('Eligibility check useEffect triggered:', {
      school: school?.id,
      currentUser: currentUser?.uid,
      schoolsLoading,
      hasUser: !!currentUser,
      hasSchool: !!school,
      userCourses: currentUser?.courses ? Object.keys(currentUser.courses) : []
    });
    
    if (school && currentUser && !schoolsLoading) {
      console.log('Ensuring eligibility is calculated for school:', school.name);
      if (!isEligibilityCalculatedForSchools([school.name])) {
        calculateEligibilityForSchools([school.name]);
      }
    }
  }, [school?.id, currentUser?.uid, schoolsLoading, currentUser?.courses, calculateEligibilityForSchools, isEligibilityCalculatedForSchools]);

  // Sync filter state with URL parameters when component mounts or URL changes
  useEffect(() => {
    const urlStatus = searchParams.get('status');
    const urlCategory = searchParams.get('category');
    const urlProgram = searchParams.get('program');

    console.log('🔄 State sync useEffect triggered:', {
      urlStatus,
      urlCategory,
      urlProgram,
      currentStatusFilter: statusFilter,
      currentCategoryFilter: categoryFilter,
      currentProgramNameFilter: programNameFilter
    });

    // Handle status filter - if URL has no status parameter, it should be 'All'
    if (urlStatus === null && statusFilter !== 'All') {
      console.log('🔄 Setting statusFilter to All (URL has no status parameter)');
      setStatusFilter('All');
    } else if (urlStatus !== null && urlStatus !== statusFilter) {
      console.log('🔄 Setting statusFilter to URL value:', urlStatus);
      setStatusFilter(urlStatus);
    }
    
    // Handle category filter - if URL has no category parameter, it should be empty
    if (urlCategory === null && categoryFilter !== '') {
      console.log('🔄 Setting categoryFilter to empty (URL has no category parameter)');
      setCategoryFilter('');
    } else if (urlCategory !== null && urlCategory !== categoryFilter) {
      console.log('🔄 Setting categoryFilter to URL value:', urlCategory);
      setCategoryFilter(urlCategory);
    }
    
    // Handle program filter - if URL has no program parameter, it should be empty
    if (urlProgram === null && programNameFilter !== '') {
      console.log('🔄 Setting programNameFilter to empty (URL has no program parameter)');
      setProgramNameFilter('');
    } else if (urlProgram !== null && urlProgram !== programNameFilter) {
      console.log('🔄 Setting programNameFilter to URL value:', urlProgram);
      setProgramNameFilter(urlProgram);
    }
  }, [searchParams]);

  // Note: Removed clearEligibility() from cleanup to preserve data across navigation
  // This allows eligibility data to persist when navigating to program detail pages

  const handleSchoolClick = () => {
    navigate(`/schools/${id}`);
  };

  // Helper function to get eligibility status for a program from the Programs Context
  const getEligibilityStatus = (program: any) => {
    // First, try to get eligibility from the cache (primary source)
    const cachedEligibility = getProgramEligibility(program.id);
    
    // Fallback to program.eligibility if cache doesn't have it (backwards compatibility)
    const eligibility = cachedEligibility || program?.eligibility;
    
    if (!eligibility) {
      return null;
    }
    
    // Determine which field to use based on source
    // Cache uses ifEligible, program.eligibility uses isEligible
    const isEligible = cachedEligibility ? cachedEligibility.ifEligible : eligibility.isEligible;
    
    // If isEligible is explicitly false, return false
    if (isEligible === false) {
      return false;
    }
    
    // If isEligible is explicitly true, return true
    if (isEligible === true) {
      return true;
    }
    
    // If isEligible is null, it means there are some issues.
    // We need to determine if these issues make it "Not Yet Eligible" or "Other".
    if (isEligible === null) {
      const hasMissingCourses = (eligibility.missingCourses?.length || 0) > 0;
      const hasGradeIssues = (eligibility.gradeIssues?.length || 0) > 0;
      
      const userGrade = currentUser?.courses ? 
        (Object.values(currentUser.courses).some((course: any) => course.grade === '12' || course.grades === 12) ? '12' :
         Object.values(currentUser.courses).some((course: any) => course.grade === '11' || course.grades === 11) ? '11' : '10') : '10';
      
      const hasLowAverage = userGrade === '12' &&
                           eligibility.calculatedAverage !== undefined &&
                           eligibility.programAverage !== undefined &&
                           eligibility.calculatedAverage < eligibility.programAverage;

      // If there are any specific reasons for not being eligible (missing courses, grade issues, or low average for G12),
      // then it should be classified as "Not Yet Eligible" (false).
      if (hasMissingCourses || hasGradeIssues || hasLowAverage) {
        return false;
      }
    }
    
    // Default to null for other cases
    return null;
  };

  // Sort programs by category first, then by program name
  const sortedPrograms = [...schoolPrograms].sort((a, b) => {
    // First sort by category
    const categoryComparison = (a.category || '').localeCompare(b.category || '');
    if (categoryComparison !== 0) {
      return categoryComparison;
    }
    // Then sort by program name
    return a.program.localeCompare(b.program);
  });

  // Filter programs based on user selections
  const filteredPrograms = sortedPrograms.filter(program => {
    // Status filter
    if (statusFilter !== 'All') {
      const eligibilityStatus = getEligibilityStatus(program);
      
      if (statusFilter === 'Eligible') {
        // Show only programs that are eligible (true)
        if (eligibilityStatus !== true) return false;
      } else if (statusFilter === 'Not Yet Eligible') {
        // Show only programs that are not eligible (false)
        if (eligibilityStatus !== false) return false;
      } else if (statusFilter === 'Other') {
        // Show only programs with unclear status (null)
        if (eligibilityStatus !== null) return false;
      }
    }

    // Category filter
    if (categoryFilter && !program.category?.toLowerCase().includes(categoryFilter.toLowerCase())) {
      return false;
    }

    // Program name filter
    if (programNameFilter && !program.program.toLowerCase().includes(programNameFilter.toLowerCase())) {
      return false;
    }

    return true;
  });

  // Pagination logic for mobile programs view
  const paginatedPrograms = isMobile 
    ? filteredPrograms.slice((programsPage - 1) * PROGRAMS_PER_PAGE, programsPage * PROGRAMS_PER_PAGE)
    : filteredPrograms;
  
  const totalProgramsPages = isMobile ? Math.ceil(filteredPrograms.length / PROGRAMS_PER_PAGE) : 1;

  // Clear all filters
  const clearFilters = () => {
    console.log('🔄 Clear Filters clicked - before clearing:', {
      currentStatusFilter: statusFilter,
      currentCategoryFilter: categoryFilter,
      currentProgramNameFilter: programNameFilter,
      urlStatus: searchParams.get('status'),
      urlCategory: searchParams.get('category'),
      urlProgram: searchParams.get('program')
    });
    
    // Clear all filters in a single operation to avoid useEffect interference
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('status');
    newSearchParams.delete('category');
    newSearchParams.delete('program');
    
    // Update state directly
    setStatusFilter('All');
    setCategoryFilter('');
    setProgramNameFilter('');
    
    // Update URL and localStorage
    setSearchParams(newSearchParams);
    localStorage.setItem(`schoolProfile_${id}_status`, 'All');
    localStorage.setItem(`schoolProfile_${id}_category`, '');
    localStorage.setItem(`schoolProfile_${id}_program`, '');
    
    console.log('🔄 Clear Filters clicked - after clearing:', {
      newStatusFilter: 'All',
      newCategoryFilter: '',
      newProgramNameFilter: '',
      newSearchParams: newSearchParams.toString()
    });
  };

  // Handle program page changes
  const handleProgramPageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setProgramsPage(value);
  };

  // Reset pagination when filters change
  useEffect(() => {
    setProgramsPage(1);
  }, [statusFilter, categoryFilter, programNameFilter]);

  // Removed getPageLabels function as pagination is no longer needed

  // Removed: filteredScholarships, paginatedScholarships

  // Pagination for favorite students
  // const studentsPageCount = Math.ceil(favoriteStudents.length / STUDENTS_PER_PAGE);

  if (schoolsLoading || loading || programsLoading /* scholarshipsLoading removed */) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (schoolsError || error || programsError /* scholarshipsError removed */ || !school) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{schoolsError || error || programsError /* scholarshipsError removed */ || 'School not found'}</Alert>
        <Button variant="outlined" sx={{ mt: 2 }} onClick={handleBackNavigation}>
          &larr; Back
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', gap: 4, flexDirection: { xs: 'column', lg: 'row' } }}>
      {/* Main Content */}
      <Box sx={{ flex: 2 }}>
        <Button variant="outlined" sx={{ mt: 3, mb: 3 }} onClick={handleBackNavigation}>
          &larr; Back
        </Button>

        {/* School Header */}
        <Paper sx={{ p: { xs: 3, sm: 3 }, mb: 2 }}>
          {/* School Image - Full Width with Overlaid Logo */}
          <Box sx={{ mb: 3, position: 'relative' }}>
            <img
              src={school.image}
              alt={school.name}
              style={{ 
                width: '100%', 
                height: isMobile ? 200 : 250, 
                objectFit: 'cover', 
                borderRadius: 8,
                display: 'block'
              }}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = 'https://via.placeholder.com/800x250/1976d2/ffffff?text=School+Image';
              }}
            />
            {/* Results Image (School Logo) - Overlaid */}
            {school.resultsImage && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: -30,
                  left: 20,
                  width: isMobile ? 80 : 100,
                  height: isMobile ? 80 : 100,
                  borderRadius: '50%',
                  border: '4px solid white',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  overflow: 'hidden',
                  backgroundColor: 'white'
                }}
              >
                <img
                  src={school.resultsImage}
                  alt={`${school.name} Logo`}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block'
                  }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = 'https://via.placeholder.com/100x100/1976d2/ffffff?text=Logo';
                  }}
                />
              </Box>
            )}
          </Box>
          
          {/* School Name and Details */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'flex-start', 
            mb: 3, 
            gap: 2, 
            flexWrap: 'nowrap',
            mt: school.resultsImage ? (isMobile ? 6 : 7) : 0
          }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'flex-start', 
                gap: 2, 
                mb: 1, 
                flexWrap: 'nowrap'
              }}>
                <Typography 
                  variant={isMobile ? 'h5' : 'h4'} 
                  gutterBottom 
                  sx={{ 
                    mb: 0,
                    fontSize: isMobile ? '1.5rem' : '2.125rem',
                    lineHeight: isMobile ? '1.2' : '1.235',
                    wordBreak: 'break-word',
                    flex: 1,
                    minWidth: 0
                  }}
                >
                  {school.name}
                </Typography>
                <Tooltip title={isFavorite ? `Remove from ${getFavouritesLabel().toLowerCase()}` : `Add to ${getFavouritesLabel().toLowerCase()}`}>
                  <IconButton 
                    onClick={toggleFavorite}
                    disabled={favoriteLoading}
                    sx={{ 
                      color: isFavorite ? '#8dd439' : 'grey.400',
                      flexShrink: 0,
                      '&:hover': {
                        color: isFavorite ? '#7bc536' : 'grey.600',
                      }
                    }}
                  >
                    {favoriteLoading ? (
                      <CircularProgress size={36} />
                    ) : isFavorite ? (
                      <Star sx={{ fontSize: '36px' }} />
                    ) : (
                      <StarBorder sx={{ fontSize: '36px' }} />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography variant="subtitle1" color="text.secondary" gutterBottom sx={{ wordBreak: 'break-word' }}>
                {school.SchoolLocationLatitude && school.SchoolLocationLongitude ? (
                  <Link
                    component="button"
                    variant="body1"
                    onClick={() => setMapModalOpen(true)}
                    sx={{
                      color: 'text.secondary',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      '&:hover': {
                        color: 'primary.main',
                      },
                    }}
                  >
                    {school.city}, {school.province}
                  </Link>
                ) : (
                  `${school.city}, ${school.province}`
                )}
                {distance !== null && (
                  <span style={{ marginLeft: 12, color: 'inherit' }}>
                    &bull; {distanceLoading ? (
                      <CircularProgress size={12} sx={{ verticalAlign: 'middle', ml: 0.5 }} />
                    ) : (
                      `${formatDistance(distance)} away`
                    )}
                  </span>
                )}
                {school.SchoolPhone && (
                  <span style={{ marginLeft: 12, color: 'inherit' }}>
                    &bull; {school.SchoolPhone}
                  </span>
                )}
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                gap: 1, 
                flexWrap: 'wrap', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mt: 1
              }}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
                  <Chip label={`${Number(school.enrollment).toLocaleString()} students`} size="small" />
                  {school.website && (
                    <Chip 
                      label="Website" 
                      size="small" 
                      clickable 
                      onClick={handleSchoolWebsiteClick}
                    />
                  )}
                  {school.SchoolCatalog && (
                    <Chip 
                      label="Viewbook" 
                      size="small" 
                      clickable 
                      onClick={handleSchoolCatalogClick}
                    />
                  )}
                  {school.SchoolVirtualTourURL && (
                    <Chip 
                      label="View Virtual Tour" 
                      size="small" 
                      clickable 
                      onClick={handleVirtualTourClick}
                    />
                  )}
                  {school.SchoolCampusTourURL && (
                    <Chip 
                      label="Book a Campus Tour" 
                      size="small" 
                      clickable 
                      onClick={handleCampusTourClick}
                    />
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                  {school.SchoolTwitterURL && (
                    <Tooltip title="X (Twitter)">
                      <IconButton onClick={handleSchoolTwitterClick} sx={{ color: 'primary.main' }}>
                        <XIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {school.SchoolInstagramURL && (
                    <Tooltip title="Instagram">
                      <IconButton onClick={handleSchoolInstagramClick} sx={{ color: 'primary.main' }}>
                        <InstagramIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {school.SchoolWikiURL && (
                    <Tooltip title="Wikipedia">
                      <IconButton onClick={handleSchoolWikipediaClick} sx={{ color: 'primary.main' }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm.01 3.5c.82 0 1.49.67 1.49 1.5s-.67 1.5-1.49 1.5c-.82 0-1.49-.67-1.49-1.5s.67-1.5 1.49-1.5zm2.99 13h-2.01v-1.5h2.01V18.5zm2.5-2.5h-1.5v-1.5h1.5V16zm-10 0H5.5v-1.5H7V16zm2.5-2.5H7v-1.5h2.5V13.5zm5 0h-2.5v-1.5H17V13.5zm-2.5-2.5h-2.01V9.5h2.01V11zm-2.5-2.5H7V7.5h2.5V8.5zm5 0h-2.5V7.5H17V8.5z" fill="#000"/></svg>
                      </IconButton>
                    </Tooltip>
                  )}
                  {school.SchoolYoutubeURL && (
                    <Tooltip title="YouTube">
                      <IconButton onClick={handleSchoolYouTubeClick} sx={{ color: 'primary.main' }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M21.8 8.001a2.75 2.75 0 0 0-1.94-1.94C18.2 6 12 6 12 6s-6.2 0-7.86.06A2.75 2.75 0 0 0 2.2 8.001C2.14 9.66 2.14 12 2.14 12s0 2.34.06 3.999a2.75 2.75 0 0 0 1.94 1.94C5.8 18 12 18 12 18s6.2 0 7.86-.06a2.75 2.75 0 0 0 1.94-1.94c.06-1.66.06-3.999.06-3.999s0-2.34-.06-3.999zM9.75 15.02V8.98l6.5 3.02-6.5 3.02z" fill="#f00"/></svg>
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </Paper>

        {/* Tabs for main sections */}
        <Paper sx={{ p: 2, mb: 4 }}>
          <Tabs
            value={selectedTab}
            onChange={(_, newValue) => setSelectedTab(newValue)}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
            sx={{ mb: 3 }}
          >
            <Tab label="Programs" />
            <Tab label="Events" />
            {/* <Tab label="Scholarships" /> */}
          </Tabs>
          {/* Programs Tab */}
          {selectedTab === 0 && (
            <>
              {/* Eligibility notice */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  Estimated eligibility is customized for you based on the courses and grades entered in your{' '}
                  <Link
                    component="button"
                    onClick={() => navigate('/my-student-profile?tab=academics')}
                    sx={{
                      color: 'primary.main',
                      textDecoration: 'none',
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      font: 'inherit',
                      cursor: 'pointer',
                    }}
                  >
                    profile
                  </Link>
                  .
                </Typography>
              </Box>

              {eligibilityLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2 }}>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Checking program eligibility...
                  </Typography>
                </Box>
              )}
              
              {eligibilityError && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {eligibilityError.includes('update your profile') ? (
                    <Box>
                      No course data found. Please{' '}
                      <Typography
                        component="span"
                        variant="body2"
                        color="primary"
                        sx={{ 
                          cursor: 'pointer', 
                          textDecoration: 'underline',
                          fontWeight: 'inherit'
                        }}
                        onClick={() => navigate('/my-student-profile?tab=academics')}
                      >
                        update your profile
                      </Typography>
                      {' '}with courses and grades to view program eligibility.
                    </Box>
                  ) : (
                    eligibilityError
                  )}
                </Alert>
              )}

              {schoolPrograms.length > 0 ? (
                <>
                  {/* Filter Bar */}
                  <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
                    {/* First row: Filter controls */}
                    <Box sx={{ 
                      display: 'flex', 
                      gap: 2, 
                      alignItems: 'flex-end', 
                      flexWrap: { xs: 'wrap', md: 'nowrap' },
                      flexDirection: { xs: 'column', md: 'row' }
                    }}>
                      {/* Status Filter */}
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1, 
                        minWidth: { xs: '100%', md: 120 },
                        flex: { md: '0 0 auto' }
                      }}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                          Status:
                        </Typography>
                        <FormControl size="small" sx={{ width: '100px' }}>
                          <Select
                            value={statusFilter}
                            onChange={(e) => updateStatusFilter(e.target.value)}
                            sx={{ 
                              bgcolor: 'white',
                              '& .MuiSelect-select': {
                                fontSize: '0.75rem',
                                py: 0.5,
                              },
                            }}
                          >
                            <MenuItem value="All" sx={{ fontSize: '0.75rem' }}>All</MenuItem>
                            <MenuItem value="Eligible" sx={{ fontSize: '0.75rem' }}>Eligible</MenuItem>
                            <MenuItem value="Not Yet Eligible" sx={{ fontSize: '0.75rem' }}>Not Yet Eligible</MenuItem>
                            <MenuItem value="Other" sx={{ fontSize: '0.75rem' }}>Other</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>

                      {/* Program Name Filter */}
                      <Box sx={{ 
                        minWidth: { xs: '100%', md: 200 },
                        flex: { md: '1 1 auto' }
                      }}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', mb: 0.5, fontSize: '0.75rem' }}>
                          Program Name:
                        </Typography>
                        <TextField
                          size="small"
                          placeholder="Filter by program name..."
                          value={programNameFilter}
                          onChange={(e) => updateProgramNameFilter(e.target.value)}
                          sx={{ 
                            bgcolor: 'white',
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                borderColor: 'grey.300',
                              },
                              '& input': {
                                fontSize: '0.75rem',
                                py: 0.5,
                              },
                            },
                          }}
                        />
                      </Box>

                      {/* Category Filter */}
                      <Box sx={{ 
                        minWidth: { xs: '100%', md: 200 },
                        flex: { md: '1 1 auto' }
                      }}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', mb: 0.5, fontSize: '0.75rem' }}>
                          Category:
                        </Typography>
                        <TextField
                          size="small"
                          placeholder="Filter by category..."
                          value={categoryFilter}
                          onChange={(e) => updateCategoryFilter(e.target.value)}
                          sx={{ 
                            bgcolor: 'white',
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                borderColor: 'grey.300',
                              },
                              '& input': {
                                fontSize: '0.75rem',
                                py: 0.5,
                              },
                            },
                          }}
                        />
                      </Box>
                    </Box>
                    
                    {/* Second row: Program count and Clear Filters button */}
                    <Box sx={{ 
                      mt: 1, 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center' 
                    }}>
                      <Box sx={{ pl: 4 }}>
                        <Typography variant="caption" color="text.secondary">
                          {filteredPrograms.length} program{filteredPrograms.length !== 1 ? 's' : ''}
                        </Typography>
                      </Box>
                      <Button
                        variant="contained"
                        onClick={clearFilters}
                        sx={{
                          bgcolor: 'grey.700',
                          color: 'white',
                          '&:hover': {
                            bgcolor: 'grey.800',
                          },
                          borderRadius: 1,
                          px: 2,
                          py: 0.5,
                          height: '24px',
                          fontSize: '0.75rem',
                        }}
                      >
                        Clear Filters
                      </Button>
                    </Box>
                  </Paper>

                  {/* Link to Custom Eligibility Indicators Modal */}
                  <Box sx={{ mb: 2, mt: 1 }}>
                    <Link
                      component="button"
                      onClick={() => setEligibilityIndicatorsModalOpen(true)}
                      sx={{
                        color: 'primary.main',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        font: 'inherit',
                        fontSize: '0.875rem',
                        '&:hover': {
                          color: 'primary.dark',
                        },
                      }}
                    >
                      Custom Eligibility Indicator descriptions
                    </Link>
                  </Box>

                  <TableContainer component={Paper} sx={{ boxShadow: 'none', mb: 0 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', verticalAlign: 'bottom', minWidth: 50, maxWidth: 50, width: 50 }}></TableCell>
                          <TableCell sx={{ fontWeight: 'bold', verticalAlign: 'bottom' }}>Program</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', minWidth: 280, maxWidth: 320, width: 280, verticalAlign: 'bottom' }}>Program Category</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedPrograms.map(program => (
                          <TableRow 
                            key={program.id}
                            sx={{
                              cursor: 'pointer',
                              '&:hover': { backgroundColor: 'grey.50' }
                            }}
                            onClick={() => {
                              // Preserve current filter state in URL when navigating to program details
                              const currentParams = new URLSearchParams(searchParams);
                              // Add a parameter to indicate this came from a school profile
                              currentParams.set('fromSchool', school.id);
                              navigate(`/programs/${program.id}?${currentParams.toString()}`);
                            }}
                          >
                            <TableCell sx={{ textAlign: 'center' }}>
                                              {(() => {
                        const eligibility = getProgramEligibility(program.id);
                        return eligibility ? (
                  <Tooltip 
                    title={
                      <div>
                        <div>{eligibility?.alertMessage || 'Eligibility checked'}</div>
                        {eligibility?.nextYearSuggestion && eligibility.nextYearSuggestion !== "" && (
                          <div style={{ marginTop: '8px', fontStyle: 'italic', color: '#f44336' }}>
                            💡 {eligibility?.nextYearSuggestion}
                          </div>
                        )}
                      </div>
                    }
                    componentsProps={{
                      tooltip: {
                        sx: {
                          backgroundColor: '#f5f5f5',
                          color: '#333',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                          fontSize: '14px',
                          padding: '12px',
                          maxWidth: '300px'
                        }
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      {(() => {
                        // Get user's grade from courses
                        const userGrade = currentUser?.courses ? 
                          (Object.values(currentUser.courses).some((course: any) => course.grade === '12' || course.grades === 12) ? '12' :
                           Object.values(currentUser.courses).some((course: any) => course.grade === '11' || course.grades === 11) ? '11' : '10') : '10';
                        
                        const isGrade10 = userGrade === '10';
                        if (eligibility?.ifEligible === true) {
                          if (isGrade10) {
                            // Grade 10: Fosterway green circle with white full vertical arrow for on track
                            return (
                              <Box sx={{
                                width: 24,
                                height: 24,
                                borderRadius: '50%',
                                backgroundColor: '#8dd439', // Fosterway green (lighter shade)
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                <ArrowUpwardIcon sx={{ color: 'white', fontSize: 16 }} />
                              </Box>
                            );
                          } else {
                            // Other grades: Original green check
                            return <CheckCircleIcon sx={{ color: 'success.main', fontSize: 24 }} />;
                          }
                        } else if (eligibility?.ifEligible === false) {
                          if (isGrade10) {
                            // Grade 10: Check if it's a grade issue vs missing courses
                            const hasGradeIssues = eligibility?.gradeIssues && eligibility.gradeIssues.length > 0;
                            const hasMissingCourses = eligibility?.missingCourses && eligibility.missingCourses.length > 0;
                            
                            // Check if this program has early prerequisites (like "Six of 4M, 4U")
                            const hasEarlyPrereqs = eligibility?.alertMessage?.includes("Next year's courses will be prerequisites");
                            
                            if (hasEarlyPrereqs) {
                              // Grade 10: Yellow circle with white full vertical arrow for early prerequisites
                              return (
                                <Box sx={{
                                  width: 24,
                                  height: 24,
                                  borderRadius: '50%',
                                  backgroundColor: '#ffc107', // Yellow
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}>
                                  <ArrowUpwardIcon sx={{ color: 'white', fontSize: 16 }} />
                                </Box>
                              );
                            } else if (hasGradeIssues) {
                              // Grade 10: Yellow circle with white full vertical arrow for grades need work (prioritize over missing courses)
                              return (
                                <Box sx={{
                                  width: 24,
                                  height: 24,
                                  borderRadius: '50%',
                                  backgroundColor: '#ffc107', // Yellow
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}>
                                  <ArrowUpwardIcon sx={{ color: 'white', fontSize: 16 }} />
                                </Box>
                              );
                            } else if (hasMissingCourses) {
                              // Grade 10: Blue circle with white undo arrow for missing prerequisites (only if no grade issues)
                              return (
                                <Box sx={{
                                  width: 24,
                                  height: 24,
                                  borderRadius: '50%',
                                  backgroundColor: 'primary.main',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}>
                                  <UndoIcon sx={{ color: 'white', fontSize: 16 }} />
                                </Box>
                              );
                            } else {
                              // Grade 10: Fallback - should not happen but just in case
                              return (
                                <Box sx={{
                                  width: 24,
                                  height: 24,
                                  borderRadius: '50%',
                                  backgroundColor: 'primary.main',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}>
                                  <UndoIcon sx={{ color: 'white', fontSize: 16 }} />
                                </Box>
                              );
                            }
                          } else {
                            // Other grades: Check if this is a Grade 12 student with low average
                            if (userGrade === '12' && 
                                eligibility?.calculatedAverage && 
                                eligibility?.programAverage && 
                                eligibility.calculatedAverage < eligibility.programAverage && 
                                (!eligibility?.missingCourses || eligibility.missingCourses.length === 0) && 
                                (!eligibility?.gradeIssues || eligibility.gradeIssues.length === 0)) {
                              // Grade 12: Blue circle with split arrow for low average (has courses but average too low)
                              return (
                                <Box sx={{
                                  width: 24,
                                  height: 24,
                                  borderRadius: '50%',
                                  backgroundColor: 'primary.main',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}>
                                  <AltRouteIcon sx={{ color: 'white', fontSize: 16 }} />
                                </Box>
                              );
                            } else {
                              // Other grades: Original blue circle with split arrow
                              return (
                                <Box sx={{
                                  width: 24,
                                  height: 24,
                                  borderRadius: '50%',
                                  backgroundColor: 'primary.main',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}>
                                  <AltRouteIcon sx={{ color: 'white', fontSize: 16 }} />
                                </Box>
                              );
                            }
                          }
                        } else {
                          // isEligible is null - check if there are missing courses or grade issues
                          const hasGradeIssues = eligibility?.gradeIssues && eligibility.gradeIssues.length > 0;
                          const hasMissingCourses = eligibility?.missingCourses && eligibility.missingCourses.length > 0;
                          
                          // Check if this is a Grade 12 student with low average (even when isEligible is null)
                          if (userGrade === '12' && 
                              eligibility?.calculatedAverage && 
                              eligibility?.programAverage && 
                              eligibility.calculatedAverage < eligibility.programAverage &&
                              (!hasMissingCourses) &&
                              (!hasGradeIssues)) {
                            // Grade 12: Blue circle with split arrow for low average (has courses but average too low)
                            return (
                              <Box sx={{
                                width: 24,
                                height: 24,
                                borderRadius: '50%',
                                backgroundColor: 'primary.main',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                <AltRouteIcon sx={{ color: 'white', fontSize: 16 }} />
                              </Box>
                            );
                          } else if (hasGradeIssues || hasMissingCourses) {
                            // Show blue split arrow for missing courses or grade issues
                            return (
                              <Box sx={{
                                width: 24,
                                height: 24,
                                borderRadius: '50%',
                                backgroundColor: 'primary.main',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                <AltRouteIcon sx={{ color: 'white', fontSize: 16 }} />
                              </Box>
                            );
                          } else {
                            // No issues found - show green checklist
                            return (
                              <Box sx={{
                                width: 24,
                                height: 24,
                                borderRadius: '50%',
                                backgroundColor: '#8dd439',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                <AssignmentIcon sx={{ color: 'white', fontSize: 16 }} />
                              </Box>
                            );
                          }
                        }
                      })()}
                    </Box>
                  </Tooltip>
                ) : (
                  <Tooltip title="Eligibility not checked">
                    <HelpIcon sx={{ color: 'grey.400', fontSize: 20 }} />
                  </Tooltip>
                );
                      })()}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 500 }}>{program.program}</TableCell>
                            <TableCell sx={{ fontWeight: 500 }}>{program.category || 'N/A'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  {/* Pagination for mobile only */}
                  {isMobile && totalProgramsPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                      <Pagination
                        count={totalProgramsPages}
                        page={programsPage}
                        onChange={handleProgramPageChange}
                        size="small"
                        color="primary"
                        showFirstButton
                        showLastButton
                      />
                    </Box>
                  )}
                </>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  No programs found for this school.
                </Typography>
              )}
            </>
          )}
          {/* Events Tab */}
          {selectedTab === 1 && (
            <>
              {eventsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                  <CircularProgress />
                </Box>
              ) : (
                (() => {
                  // Filter and sort events for this school
                  const now = new Date();
                  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()); // Start of today
                  const schoolEvents = allEvents
                    .filter(event => (event as any).hostName === school.name)
                    .filter(event => {
                      const eventDate = new Date(((event as any).startTime || '').replace(' ', 'T'));
                      return !isNaN(eventDate.getTime()) && eventDate >= today;
                    })
                    .sort((a, b) => {
                      const dateA = new Date(((a as any).startTime || '').replace(' ', 'T'));
                      const dateB = new Date(((b as any).startTime || '').replace(' ', 'T'));
                      return dateA.getTime() - dateB.getTime(); // Oldest first (ascending)
                    });
                  return (
                    <TableContainer component={Paper} sx={{ boxShadow: 'none', mb: 2 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', minWidth: 200, maxWidth: 200, width: 200 }}>Date</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Event Title</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Location</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', minWidth: 120, maxWidth: 120, width: 120 }}></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {schoolEvents.length > 0 ? schoolEvents.map(event => {
                            // Check if event is added to user's calendar
                            const isAddedToCalendar = currentUser?.profileExtra?.eventsAddedToCalendar?.[event.id] && 
                              !currentUser.profileExtra.eventsAddedToCalendar[event.id].timestampCancelled;
                            
                            return (
                              <TableRow 
                                key={event.id}
                                sx={{
                                  cursor: 'pointer',
                                  '&:hover': { backgroundColor: 'grey.50' }
                                }}
                                onClick={() => navigate(`/events/${event.id}`, { state: { event, fromSchoolProfile: true, selectedTab: selectedTab } })}
                              >
                                <TableCell sx={{ verticalAlign: 'top' }}>{formatEventDateWithTime((event as any).startTime)}</TableCell>
                                <TableCell sx={{ verticalAlign: 'top' }}>{(event as any).title}</TableCell>
                                <TableCell sx={{ verticalAlign: 'top' }}>{(event as any).address || (event as any).location || 'TBD'}</TableCell>
                                <TableCell sx={{ verticalAlign: 'top', textAlign: 'center' }}>
                                  {isAddedToCalendar && (
                                    <Chip 
                                      label="Added to Calendar" 
                                      size="small" 
                                      color="success" 
                                      variant="outlined"
                                      sx={{ fontSize: '0.75rem' }}
                                    />
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          }) : (
                            <TableRow>
                              <TableCell colSpan={4} align="center">No events found for this school.</TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  );
                })()
              )}
            </>
          )}
          {/* Scholarships Tab */}
          {/*
          {selectedTab === 2 && (
            // Scholarships tab code block removed
          )}
          */}

        </Paper>
      </Box>

      {/* Sidebar */}
      <Box sx={{ flex: 1, position: 'sticky', top: 100 }}>
        {/* Admissions Minimum Requirements */}
        <Paper sx={{ p: 3, mb: 4, mt: { lg: 7.5 } }}>
          {school.admissionsMinimum && 
           school.admissionsMinimum.trim() !== '' && 
           school.admissionsMinimum !== '0' && 
           parseInt(school.admissionsMinimum) > 50 && (
            <Typography variant="h6" gutterBottom sx={{ textAlign: 'center' }}>Estimated Admissions Minimum</Typography>
          )}
          {school.admissionsMinimum && 
           school.admissionsMinimum.trim() !== '' && 
           school.admissionsMinimum !== '0' && 
           parseInt(school.admissionsMinimum) > 50 && (
            <Typography variant="h4" color="primary" sx={{ fontWeight: 700, textAlign: 'center' }}>
              {school.admissionsMinimum}%
            </Typography>
          )}
          <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'left', mt: 1 }}>
            Admission requirements for each individual program will vary. For a more accurate estimate, please refer to the program requirements.
          </Typography>
        </Paper>
        
        {/* General Admission Requirements Section */}
        {school.admissionRequirements && school.admissionRequirements.length > 0 && (
          <Paper sx={{ p: 3, mb: 4 }}>
            <Typography variant="h6" gutterBottom>General Admission Requirements</Typography>
            <Typography variant="body1" color="text.secondary">
              {Array.isArray(school.admissionRequirements)
                ? school.admissionRequirements.join(' ')
                : school.admissionRequirements}
            </Typography>
          </Paper>
        )}
        
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" gutterBottom>Snapshot</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
            <SchoolIcon />
            <Typography variant="body1" color="text.secondary">
              {schoolPrograms.length} Programs
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
            <Event />
            <Typography variant="body1" color="text.secondary">
              {allEvents.filter(event => {
                if ((event as any).hostName !== school.name) return false;
                const now = new Date();
                const ninetyDaysFromNow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 90);
                const eventDate = new Date(((event as any).startTime || '').replace(' ', 'T'));
                return !isNaN(eventDate.getTime()) && eventDate >= now && eventDate <= ninetyDaysFromNow;
              }).length} Upcoming Events
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
            <AttachMoney />
            <Typography variant="body1" color="text.secondary">
              {/* {schoolScholarships.length} Scholarships */}
              Scholarship data coming soon
            </Typography>
          </Box>
          {school.primaryLanguage && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, gap: 1 }}>
              <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                Language:
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {school.primaryLanguage}
              </Typography>
            </Box>
          )}
        </Paper>
        {/* Only show Varsity Athletics section if there are sports to display */}
        {!athleticsLoading && athletics.length > 0 && (
          <Paper sx={{ p: 3, mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Varsity Athletics{school.athleticsNickname ? ` (${school.athleticsNickname})` : ''}
            </Typography>
            {school.athleticsWebsite && (
              <Box sx={{ mb: 2 }}>
                <Chip
                  label="Website" 
                  clickable
                  onClick={handleAthleticsWebsiteClick}
                />
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
              {/* Male Column */}
              <Box sx={{ flex: 1, bgcolor: 'grey.50', borderRadius: 2, p: 2, boxShadow: 1, minWidth: 140 }}>
                {(() => {
                  const maleSports = athletics.filter(a => a.gender === 'Male').map(a => a.sport).sort();
                  return (
                    <Typography variant="subtitle2" gutterBottom align="left" sx={{ fontWeight: 600, letterSpacing: 1 }}>
                      Male{maleSports.length > 0 ? ` (${maleSports.length})` : ''}
                    </Typography>
                  );
                })()}
                {(() => {
                  const maleSports = athletics.filter(a => a.gender === 'Male').map(a => a.sport).sort();
                  return maleSports.length > 0 ? (
                    <List dense sx={{ pl: 2 }}>
                      {maleSports.map((sport, idx) => (
                        <ListItem key={idx} sx={{ py: 0.5, px: 0 }} disableGutters>
                          <ListItemText primary={sport} primaryTypographyProps={{ fontSize: 16 }} />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.secondary" align="center">No male sports.</Typography>
                  );
                })()}
              </Box>
              {/* Female Column */}
              <Box sx={{ flex: 1, bgcolor: 'grey.50', borderRadius: 2, p: 2, boxShadow: 1, minWidth: 140 }}>
                {(() => {
                  const femaleSports = athletics.filter(a => a.gender === 'Female').map(a => a.sport).sort();
                  return (
                    <Typography variant="subtitle2" gutterBottom align="left" sx={{ fontWeight: 600, letterSpacing: 1 }}>
                      Female{femaleSports.length > 0 ? ` (${femaleSports.length})` : ''}
                    </Typography>
                  );
                })()}
                {(() => {
                  const femaleSports = athletics.filter(a => a.gender === 'Female').map(a => a.sport).sort();
                  return femaleSports.length > 0 ? (
                    <List dense sx={{ pl: 2 }}>
                      {femaleSports.map((sport, idx) => (
                        <ListItem key={idx} sx={{ py: 0.5, px: 0 }} disableGutters>
                          <ListItemText primary={sport} primaryTypographyProps={{ fontSize: 16 }} />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.secondary" align="center">No female sports.</Typography>
                  );
                })()}
              </Box>
            </Box>
          </Paper>
        )}
      </Box>

      {/* Map Modal */}
      <MapModal
        open={mapModalOpen}
        onClose={() => setMapModalOpen(false)}
        eventName={school.name}
        latitude={school.SchoolLocationLatitude}
        longitude={school.SchoolLocationLongitude}
      />

      {/* Custom Eligibility Indicators Modal */}
      <CustomEligibilityIndicatorsModal
        open={eligibilityIndicatorsModalOpen}
        onClose={() => setEligibilityIndicatorsModalOpen(false)}
      />
    </Box>
  );
};

export default SchoolProfile; 